// localStorage cache for onboarding_complete. NON-AUTHORITATIVE — server profile
// is the source of truth (see hooks/use-profile.ts). This cache exists solely to
// avoid gate flicker during the initial profile fetch.
const ONBOARDING_KEY = "fomo-fi:onboarding-complete";

export function isOnboardingComplete(): boolean {
  return localStorage.getItem(ONBOARDING_KEY) === "true";
}

export function markOnboardingComplete(): void {
  localStorage.setItem(ONBOARDING_KEY, "true");
}

export function clearOnboardingComplete(): void {
  localStorage.removeItem(ONBOARDING_KEY);
}

/**
 * What the onboarding gate should render, given what it knows about the profile.
 *
 * BEN-141 QF4. The gate used to collapse "the profile says this user has not
 * onboarded" and "the profile did not load" into the same answer — a redirect
 * into the wizard — on the reasoning that re-onboarding once is cheaper than
 * skipping onboarding. That trade is right for a user with no profile and wrong
 * for a 500: an established account was silently dropped back into setup with
 * nothing on screen saying why, which is the same silent failure the forced
 * `error` state measured on the pages behind this gate.
 *
 * Pulled out as a pure decision so the three outcomes are testable without a
 * router, a Clerk session or a query client.
 */
export type OnboardingGateOutcome = "app" | "onboarding" | "failure" | "wait";

export function onboardingGateDecision({
  status,
  hasFailed = false,
  onboardingComplete,
  billingRoute,
}: {
  status: "pending" | "success" | "error";
  /** The query has already produced an error at least once (`errorUpdateCount`). */
  hasFailed?: boolean;
  onboardingComplete: boolean;
  billingRoute: boolean;
}): OnboardingGateOutcome {
  // Stripe sends the user back to /payment and /payment/success, and Plaid to
  // /banks/oauth-return, mid-onboarding by design. Those routes are reachable
  // in every terminal state below, so there is nothing for them to wait FOR,
  // and waiting is not free: measured on the deployed dev host with
  // `/api/profile/me/` failing, the in-flight branch rendered nothing while the
  // resolved branch rendered the app, the two alternated, and the page sat at
  // zero characters through 23 profile requests in a single load. /dashboard,
  // which reaches one terminal state and stays there, made two. So the exempt
  // routes decide first (BEN-141 QF4).
  if (billingRoute) return "app";

  // No answer yet: wait, whatever the localStorage hint says.
  //
  // The gate used to redirect to the wizard while the profile was in flight
  // unless localStorage already said onboarding was finished. That hint is
  // absent on a new device, in a private window, and after any storage clear,
  // so an established account was sent into setup before the server had said a
  // word. Measured on the deployed dev host while `/api/profile/me/` was
  // answering 429: every gated route landed on /onboarding, for a user who
  // finished onboarding months ago. Waiting costs a genuinely new user a few
  // hundred milliseconds before the wizard; guessing costs an existing user
  // their place in the app.
  //
  // "Pending" is not always "not asked yet". React Query puts a query that has
  // no data back to `pending` the moment a new fetch starts, so a failed
  // profile flips error -> pending -> error on every retry, and a gate that
  // reads the middle of that as "wait" tears down whatever it is showing each
  // time round. That is the loop: the shell mounts, its components observe the
  // same failed query, observing it starts a retry, the retry says pending and
  // the shell is thrown away again. Measured at eleven layouts and twenty-three
  // profile requests on one load of /dashboard (BEN-141 QF8). Once the account
  // has failed once, the answer stays the failure until a fetch actually
  // succeeds.
  if (status === "pending") return hasFailed ? "failure" : "wait";
  if (status === "error") return "failure";
  return onboardingComplete ? "app" : "onboarding";
}

/**
 * Account, then plan, then setup (Paul, 2026-10-14: "no route around the plan").
 *
 * With gating on, an account that has neither a plan nor an exemption goes to the plan
 * step before setup. Found in the paywall rehearsal (report 142): a new unpaid account
 * that signed in landed in setup, whose paid steps the server then refused. Demo
 * personas and bypass accounts (`is_exempt`) and anyone entitled go on to setup; with
 * gating off nothing changes. An unknown status (still loading, or failed) does not
 * redirect: the server still refuses the paid steps, so this fails open like the
 * subscription gate.
 */
export function planBeforeSetup(
  status: { gating_enabled: boolean; is_entitled: boolean; is_exempt: boolean } | undefined,
): boolean {
  return status !== undefined && status.gating_enabled && !status.is_entitled && !status.is_exempt;
}
